import React, { useEffect, useState } from 'react';
import { 
    BrowserRouter as Router, 
    Routes, 
    Route, 
    Navigate, 
    Link, 
    useLocation
} from 'react-router-dom';
import { 
    Container, 
    Box, 
    ThemeProvider, 
    createTheme, 
    CssBaseline, 
    IconButton, 
    useMediaQuery,
    Divider,
    Typography,
    Stack
} from '@mui/material';
import axios from 'axios';
import { Sponsor } from './types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { auth } from './config/firebase';
import { HelmetProvider } from 'react-helmet-async';
import Home from './components/Home';
import GorDetail from './components/GorDetail';
import Login from './components/Login';
import Profile from './components/Profile';
import CoachingClinic from './components/CoachingClinic';
import Gallery from './components/Gallery';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import About from './components/About';
import Leaderboard from './components/Leaderboard';
import Navbar from './components/Navbar';
import TurnamenIntermediate from './components/TurnamenIntermediate';
import UpdateScore from './components/UpdateScore';
import BracketGenerator from './components/BracketGenerator';
import JerseyPO from './components/JerseyPO';
import TermsAndConditions from './components/TermsAndConditions';
import BookingSuccess from './components/BookingSuccess';
import BookingPending from './components/BookingPending';
import PaymentFailed from './components/PaymentFailed';
import CoachingClinicBooking from './components/CoachingClinicBooking';
import Matchmaking from './components/Matchmaking';
import Marketplace from './components/Marketplace';
import Leveling from './components/Leveling';
import OpenTournament from './components/OpenTournament';
import Member from './components/Member';
import FloatingBookingStatus from './components/FloatingBookingStatus';
import UpdateRankedMatches from './components/UpdateRankedMatches';
import LocalRankedMatch from './components/LocalRankedMatch';
import BackgroundMusicPlayer from './components/BackgroundMusicPlayer';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FriendlyMatch from './components/FriendlyMatch';
import SportTourism from './components/SportTourism';

// Komponen untuk mengecek autentikasi
const AuthCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isAuthenticated = !!localStorage.getItem('user');
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// Komponen utama yang menggunakan Router
const AppContent: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('user'));
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    
    // Baca preferensi mode dari localStorage, jika tidak ada gunakan preferensi sistem
    const getInitialMode = (): 'light' | 'dark' => {
        const savedMode = localStorage.getItem('themeMode') as 'light' | 'dark' | null;
        if (savedMode === 'light' || savedMode === 'dark') {
            return savedMode;
        }
        // Jika tidak ada preferensi tersimpan, gunakan preferensi sistem
        return prefersDarkMode ? 'dark' : 'light';
    };
    
    const [mode, setMode] = useState<'light' | 'dark'>(() => getInitialMode());
    const [sponsors, setSponsors] = useState<Sponsor[]>([]);

    const location = useLocation();

    const theme = React.useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: { 
                        main: mode === 'light' ? '#1976d2' : '#64b5f6',
                        light: mode === 'light' ? '#42a5f5' : '#90caf9',
                        dark: mode === 'light' ? '#1565c0' : '#1976d2',
                    },
                    secondary: { 
                        main: mode === 'light' ? '#ff4081' : '#f48fb1',
                        light: mode === 'light' ? '#ff79b0' : '#f8bbd9',
                        dark: mode === 'light' ? '#c60055' : '#ad1457',
                    },
                    ...(mode === 'light'
                        ? {
                            background: { 
                                default: '#e3f2fd',
                                paper: 'rgba(255, 255, 255, 0.95)',
                            },
                          }
                        : {
                            background: { 
                                default: '#0a1626',
                                paper: 'rgba(10, 22, 38, 0.95)',
                            },
                          }),
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)'
                                    : '#0a1626',
                                backgroundAttachment: 'fixed',
                                minHeight: '100vh',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none',
                                    zIndex: -1,
                                    background: mode === 'light'
                                        ? 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%)'
                                        : `
                                            radial-gradient(circle at 10% 30%, rgba(255,255,255,0.8) 1.5px, transparent 2px),
                                            radial-gradient(circle at 25% 70%, rgba(255,255,255,0.65) 1.2px, transparent 1.7px),
                                            radial-gradient(circle at 70% 40%, rgba(255,255,255,0.7) 1.7px, transparent 2.2px),
                                            radial-gradient(circle at 40% 90%, rgba(255,255,255,0.82) 1.3px, transparent 2.1px),
                                            radial-gradient(circle at 80% 60%, rgba(255,255,255,0.55) 1.1px, transparent 1.6px),
                                            radial-gradient(circle at 90% 20%, rgba(255,255,255,0.75) 1.8px, transparent 2.3px),
                                            radial-gradient(circle at 65% 83%, rgba(255,255,255,0.42) 1.2px, transparent 1.8px),
                                            radial-gradient(circle at 45% 55%, rgba(255,255,255,0.9) 1.6px, transparent 2.4px),
                                            radial-gradient(circle at 78% 30%, rgba(255,255,255,0.56) 1.1px, transparent 1.6px),
                                            radial-gradient(circle at 52% 10%, rgba(255,255,255,0.62) 1.5px, transparent 2.2px)
                                        `,
                                },
                                '@keyframes float': {
                                    '0%, 100%': {
                                        transform: 'translateY(0px) rotate(0deg)',
                                    },
                                    '33%': {
                                        transform: 'translateY(-30px) rotate(1deg)',
                                    },
                                    '66%': {
                                        transform: 'translateY(30px) rotate(-1deg)',
                                    },
                                },
                            },
                        },
                    },
                },
            }),
        [mode],
    );

    const toggleColorMode = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            // Simpan preferensi ke localStorage
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };



    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setIsAuthenticated(!!user);
        });

        return () => unsubscribe();
    }, []);

    // Sync dengan preferensi sistem hanya jika user belum pernah set preferensi manual
    useEffect(() => {
        const savedMode = localStorage.getItem('themeMode');
        // Jika tidak ada preferensi tersimpan, sync dengan preferensi sistem
        if (!savedMode) {
            setMode(prefersDarkMode ? 'dark' : 'light');
        }
    }, [prefersDarkMode]);

    useEffect(() => {
        const fetchSponsors = async () => {
            try {
                const response = await axios.get<Sponsor[]>('/api/bookings?action=sponsor');
                setSponsors(response.data);
            } catch (error) {
                console.error('Error fetching sponsors:', error);
                // Fallback ke hardcoded sponsor jika API error
                setSponsors([
                    { id: 1, url: 'https://i.imgur.com/NgQhTZi.png' },
                    { id: 2, url: 'https://i.imgur.com/PJADFi7.jpeg' },
                    { id: 3, url: 'https://i.imgur.com/qmXOn5z.png' }
                ]);
            }
        };

        fetchSponsors();
    }, []);

    const menuItems = [
        { text: 'Daftar GOR', icon: <HomeIcon />, path: '/' },
        { text: 'Coaching Clinic', icon: <SchoolIcon />, path: '/coaching-clinic' },
        { text: 'Ranked Match', icon: <SchoolIcon />, path: '/ranked-match' },
        { text: 'Local Ranked Match', icon: <SchoolIcon />, path: '/local-ranked-match' },
        { text: 'Gallery', icon: <PhotoLibraryIcon />, path: '/gallery' },
        { text: 'Tentang RaketerS', icon: <InfoIcon />, path: '/tentang-raketers' }
    ];

    if (isAuthenticated) {
        menuItems.push({ text: 'Profile', icon: <PersonIcon />, path: '/profile' });
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Navbar mode={mode} toggleColorMode={toggleColorMode} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: '100vh',
                    pt: '56px'
                }}>
                    <Container 
                        sx={{ 
                            mt: 4, 
                            mb: 4, 
                            flex: 1,
                            maxWidth: location.pathname === '/internal-tournament' ? '100%' : undefined,
                            ...(location.pathname === '/internal-tournament' && {
                                px: 0
                            })
                        }}
                    >
                        <Routes>
                            <Route path="/login" element={
                                !isAuthenticated ? <Login /> : (
                                    localStorage.getItem('redirectAfterLogin') 
                                        ? <Navigate to={localStorage.getItem('redirectAfterLogin') || '/'} replace />
                                        : <Navigate to="/" replace />
                                )
                            } />
                            <Route path="/profile" element={
                                <AuthCheck>
                                    <Profile />
                                </AuthCheck>
                            } />
                            <Route path="/coaching-clinic" element={<CoachingClinic />} />
                            <Route path="/coaching/:id" element={<CoachingClinicBooking />} />
                            <Route path="/gallery" element={<Gallery />} />
                            <Route path="/gor/:id" element={<GorDetail />} />
                            <Route path="/booking-sukses" element={<BookingSuccess />} />
                            <Route path="/booking-pending" element={<BookingPending />} />
                            <Route path="/payment-gagal" element={<PaymentFailed />} />
                            <Route path="/tentang-raketers" element={<About />} />
                            <Route path="/ranked-match/:playerName" element={<Leaderboard showOnlyRanked />} />
                            <Route path="/ranked-match" element={<Leaderboard showOnlyRanked />} />
                            <Route path="/local-ranked-match/:playerName" element={<LocalRankedMatch />} />
                            <Route path="/local-ranked-match" element={<LocalRankedMatch />} />
                            <Route path="/update-ranked-matches" element={<UpdateRankedMatches />} />
                            <Route path="/internal-tournament" element={<Leaderboard initialMainTab={0} showOnlyTournament />} />
                            <Route path="/liga" element={<Leaderboard initialMainTab={2} showOnlyLiga />} />
                            <Route path="/leveling" element={<Leveling />} />
                            <Route path="/turnamen-intermediate" element={<TurnamenIntermediate />} />
                            <Route path="/update-score" element={<UpdateScore />} />
                            <Route path="/bracket-generator" element={<BracketGenerator />} />
                            <Route path="/open-tournament" element={<OpenTournament />} />
                            <Route path="/jersey" element={<JerseyPO />} />
                            <Route path="/syarat-ketentuan" element={<TermsAndConditions />} />
                            <Route path="/matchmaking" element={<Matchmaking />} />
                            <Route path="/member" element={<Member />} />
                            <Route path="/marketplace" element={<Marketplace />} />
                            <Route path="/friendly-match" element={<FriendlyMatch />} />
                            <Route path="/sport-tourism" element={<SportTourism />} />
                            <Route path="/" element={<Home />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Container>
                    {(() => {
                        const path = location.pathname;
                        const show = path === '/' || path.startsWith('/gor/') || path === '/coaching-clinic' || path.startsWith('/coaching/');
                        return show ? <FloatingBookingStatus /> : null;
                    })()}

                    <BackgroundMusicPlayer mode={mode} />

                    {/* Visitor Counter - Atas Our Partner, tampil di tiap halaman */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 2, opacity: 0.7 }}>
                        <span style={{ marginBottom: 2, fontSize: 14, color: mode === 'dark' ? 'white' : 'black' }}>Jumlah Pengunjung:</span>
                        <a href="https://www.hitwebcounter.com/" target="_blank" rel="noopener noreferrer">
                          <img src="https://hitwebcounter.com/counter/counter.php?page=21448559&style=0010&nbdigits=5&type=ip&initCount=57"
                               title="Counters" alt="Counters" style={{ border: 0 }}/>
                        </a>
                    </Box>
                    {/* Our Partner Section */}
                    <Box sx={{
                        py: 4,
                        px: 2,
                        background: mode === 'light'
                            ? 'linear-gradient(135deg, rgba(227,242,253,0.8) 0%, rgba(187,222,251,0.6) 100%)'
                            : 'transparent',
                        backdropFilter: mode === 'light' ? 'blur(10px)' : 'none',
                        border: mode === 'light'
                            ? '1px solid rgba(255,255,255,0.2)'
                            : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        borderRadius: mode === 'light' ? 3 : 0,
                        mx: mode === 'light' ? 2 : 0,
                        boxShadow: mode === 'light'
                            ? '0 8px 32px rgba(25,118,210,0.1)'
                            : 'none',
                        position: 'relative',
                        ...(mode === 'dark' ? {
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '1px',
                                background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '1px',
                                background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                            },
                        } : {})
                    }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 'bold', 
                                mb: { xs: 1, sm: 1.5 },
                                textAlign: 'center',
                                background: mode === 'light'
                                    ? 'linear-gradient(45deg, #1976d2, #42a5f5, #1976d2)'
                                    : 'linear-gradient(45deg, #64b5f6, #90caf9, #64b5f6)',
                                backgroundSize: '200% 200%',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                transition: 'all 0.3s ease',
                                animation: 'shimmer 3s ease-in-out infinite',
                                '@keyframes shimmer': {
                                    '0%': {
                                        backgroundPosition: '0% 50%',
                                    },
                                    '50%': {
                                        backgroundPosition: '100% 50%',
                                    },
                                    '100%': {
                                        backgroundPosition: '0% 50%',
                                    },
                                },
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    filter: 'brightness(1.2)',
                                    animation: 'shimmer 1s ease-in-out infinite',
                                }
                            }}
                        >
                            Our Partner
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                width: '100%',
                                maxWidth: 920,
                                mx: 'auto',
                                gap: { xs: 1.5, sm: 2, md: 2.5 },
                            }}
                        >
                            {sponsors.map((sponsor, index) => (
                                <Box
                                    key={sponsor.id}
                                    sx={{
                                        flex: {
                                            xs: '0 1 124px',
                                            sm: '0 1 148px',
                                            md: '0 1 160px',
                                        },
                                        width: {
                                            xs: 124,
                                            sm: 148,
                                            md: 160,
                                        },
                                        maxWidth: '100%',
                                        height: { xs: 76, sm: 88, md: 96 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 1.5,
                                        py: 1,
                                        boxShadow: mode === 'light'
                                            ? '0 8px 24px rgba(25,118,210,0.12)'
                                            : '0 8px 24px rgba(13,71,161,0.28)',
                                        borderRadius: 2.5,
                                        background: mode === 'light'
                                            ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                            : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                        backdropFilter: 'blur(10px)',
                                        border: mode === 'light'
                                            ? '1px solid rgba(255,255,255,0.3)'
                                            : '1px solid rgba(255,255,255,0.1)',
                                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: mode === 'light'
                                                ? '0 12px 32px rgba(25,118,210,0.2)'
                                                : '0 12px 32px rgba(13,71,161,0.4)',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={sponsor.url}
                                        alt={`Partner ${index + 1}`}
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            width: 'auto',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Divider sx={{
                        my: 0,
                        borderColor: mode === 'light'
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        mx: 'auto',
                        width: '100%',
                        maxWidth: 900,
                        height: '2px',
                        background: mode === 'light'
                            ? 'linear-gradient(90deg, transparent 0%, rgba(227,242,253,0.8) 50%, transparent 100%)'
                            : 'linear-gradient(90deg, transparent 0%, rgba(13,71,161,0.8) 50%, transparent 100%)',
                        boxShadow: mode === 'light'
                            ? '0 2px 8px rgba(25,118,210,0.1)'
                            : '0 2px 8px rgba(13,71,161,0.3)',
                        animation: 'shimmer 3s ease-in-out infinite',
                        '@keyframes shimmer': {
                            '0%': {
                                opacity: 0.3,
                            },
                            '50%': {
                                opacity: 0.7,
                            },
                            '100%': {
                                opacity: 0.3,
                            },
                        },
                    }} />

                    <Box
                        component="footer"
                        sx={{
                            mt: 'auto',
                        }}
                    >
                        {/* Social Media Section */}
                        <Box
                            sx={{
                                py: 4,
                                px: 2,
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, rgba(227,242,253,0.8) 0%, rgba(187,222,251,0.6) 100%)'
                                    : 'transparent',
                                backdropFilter: mode === 'light' ? 'blur(10px)' : 'none',
                                border: mode === 'light'
                                    ? '1px solid rgba(255,255,255,0.2)'
                                    : 'none',
                                borderRadius: mode === 'light' ? 3 : 0,
                                mx: mode === 'light' ? 2 : 0,
                                boxShadow: mode === 'light'
                                    ? '0 8px 32px rgba(0,0,0,0.1)'
                                    : 'none',
                                position: 'relative',
                                ...(mode === 'dark' ? {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                                    },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                                    },
                                } : {})
                            }}
                        >
                            <Container maxWidth="sm">
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        gap: 1,
                                        background: mode === 'light'
                                            ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                                            : 'linear-gradient(135deg, #64b5f6 0%, #90caf9 100%)',
                                        p: 2,
                                        borderRadius: 3,
                                        boxShadow: mode === 'light'
                                            ? '0 8px 32px rgba(25, 118, 210, 0.3)'
                                            : '0 8px 32px rgba(13, 71, 161, 0.3)',
                                        border: mode === 'light'
                                            ? '1px solid rgba(255,255,255,0.2)'
                                            : '1px solid rgba(255,255,255,0.1)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: mode === 'light'
                                                ? '0 12px 40px rgba(25, 118, 210, 0.4)'
                                                : '0 12px 40px rgba(13, 71, 161, 0.4)',
                                        }
                                    }}>
                                        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                            <Box
                                                component="img"
                                                src="/images/Logo_trans.png"
                                                alt="RaketerS Logo"
                                                sx={{ 
                                                    height: 40,
                                                    transition: 'all 0.3s ease',
                                                    animation: 'float 4s ease-in-out infinite',
                                                    '@keyframes float': {
                                                        '0%, 100%': {
                                                            transform: 'translateY(0px)',
                                                        },
                                                        '50%': {
                                                            transform: 'translateY(-5px)',
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'scale(1.1) rotate(5deg)',
                                                        filter: 'brightness(1.2)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'scale(1.1) rotate(5deg)',
                                                            },
                                                            '50%': {
                                                                transform: 'scale(1.15) rotate(5deg)',
                                                            },
                                                            '100%': {
                                                                transform: 'scale(1.1) rotate(5deg)',
                                                            },
                                                        },
                                                    }
                                                }}
                                            />
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 'bold', 
                                                    ml: 1, 
                                                    color: '#fff',
                                                    transition: 'all 0.3s ease',
                                                    animation: 'glow 2s ease-in-out infinite alternate',
                                                    '@keyframes glow': {
                                                        '0%': {
                                                            textShadow: '0 0 5px rgba(255,255,255,0.3)',
                                                        },
                                                        '100%': {
                                                            textShadow: '0 0 15px rgba(255,255,255,0.6)',
                                                        },
                                                    },
                                                    '&:hover': {
                                                        transform: 'scale(1.05)',
                                                        textShadow: '0 0 20px rgba(255,255,255,0.8)',
                                                        animation: 'pulse 0.6s ease-in-out infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                transform: 'scale(1.05)',
                                                            },
                                                            '50%': {
                                                                transform: 'scale(1.08)',
                                                            },
                                                            '100%': {
                                                                transform: 'scale(1.05)',
                                                            },
                                                        },
                                                    }
                                                }}
                                            >
                                                RaketerS
                                            </Typography>
                                        </Link>
                                    </Box>

                                    <Stack direction="row" spacing={2}>
                                        <IconButton 
                                            component="a" 
                                            href="https://www.instagram.com/raketers_bandung?igsh=dnN4amZma2h2dXhx"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                background: mode === 'light'
                                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                                    : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                                backdropFilter: 'blur(10px)',
                                                border: mode === 'light'
                                                    ? '1px solid rgba(255,255,255,0.3)'
                                                    : '1px solid rgba(255,255,255,0.1)',
                                                boxShadow: mode === 'light'
                                                    ? '0 4px 16px rgba(25,118,210,0.1)'
                                                    : '0 4px 16px rgba(13,71,161,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px) scale(1.05)',
                                                    boxShadow: mode === 'light'
                                                        ? '0 8px 24px rgba(25,118,210,0.15)'
                                                        : '0 8px 24px rgba(13,71,161,0.4)',
                                                    background: mode === 'light'
                                                        ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                                                        : 'linear-gradient(135deg, rgba(13,71,161,0.15) 0%, rgba(21,101,192,0.08) 100%)',
                                                }
                                            }}
                                        >
                                            <InstagramIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                                        </IconButton>
                                        <IconButton 
                                            component="a" 
                                            href="https://www.tiktok.com/@raketers.bandung?_t=ZS-8wkcwrLmeqV&_r=1"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                background: mode === 'light'
                                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                                    : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                                backdropFilter: 'blur(10px)',
                                                border: mode === 'light'
                                                    ? '1px solid rgba(255,255,255,0.3)'
                                                    : '1px solid rgba(255,255,255,0.1)',
                                                boxShadow: mode === 'light'
                                                    ? '0 4px 16px rgba(25,118,210,0.1)'
                                                    : '0 4px 16px rgba(13,71,161,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px) scale(1.05)',
                                                    boxShadow: mode === 'light'
                                                        ? '0 8px 24px rgba(25,118,210,0.15)'
                                                        : '0 8px 24px rgba(13,71,161,0.4)',
                                                    background: mode === 'light'
                                                        ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                                                        : 'linear-gradient(135deg, rgba(13,71,161,0.15) 0%, rgba(21,101,192,0.08) 100%)',
                                                }
                                            }}
                                        >
                                            <Box 
                                                sx={{ 
                                                    width: 24, 
                                                    height: 24,
                                                    backgroundColor: (theme) => theme.palette.primary.main,
                                                    WebkitMaskImage: 'url(/images/tiktok-icon.svg)',
                                                    WebkitMaskRepeat: 'no-repeat',
                                                    WebkitMaskPosition: 'center',
                                                    WebkitMaskSize: 'contain',
                                                    maskImage: 'url(/images/tiktok-icon.svg)',
                                                    maskRepeat: 'no-repeat',
                                                    maskPosition: 'center',
                                                    maskSize: 'contain',
                                                }} 
                                            />
                                        </IconButton>
                                        <IconButton 
                                            component="a" 
                                            href="https://youtube.com/@raketersbandung?si=uhxsLj9szhsM9GS5"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                background: mode === 'light'
                                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                                    : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                                backdropFilter: 'blur(10px)',
                                                border: mode === 'light'
                                                    ? '1px solid rgba(255,255,255,0.3)'
                                                    : '1px solid rgba(255,255,255,0.1)',
                                                boxShadow: mode === 'light'
                                                    ? '0 4px 16px rgba(25,118,210,0.1)'
                                                    : '0 4px 16px rgba(13,71,161,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px) scale(1.05)',
                                                    boxShadow: mode === 'light'
                                                        ? '0 8px 24px rgba(25,118,210,0.15)'
                                                        : '0 8px 24px rgba(13,71,161,0.4)',
                                                    background: mode === 'light'
                                                        ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                                                        : 'linear-gradient(135deg, rgba(13,71,161,0.15) 0%, rgba(21,101,192,0.08) 100%)',
                                                }
                                            }}
                                        >
                                            <YouTubeIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                                        </IconButton>
                                        <IconButton 
                                            component="a" 
                                            href="https://bit.ly/WhatAppGrupRaketers"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                background: mode === 'light'
                                                    ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                                    : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
                                                backdropFilter: 'blur(10px)',
                                                border: mode === 'light'
                                                    ? '1px solid rgba(255,255,255,0.3)'
                                                    : '1px solid rgba(255,255,255,0.1)',
                                                boxShadow: mode === 'light'
                                                    ? '0 4px 16px rgba(25,118,210,0.1)'
                                                    : '0 4px 16px rgba(13,71,161,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px) scale(1.05)',
                                                    boxShadow: mode === 'light'
                                                        ? '0 8px 24px rgba(25,118,210,0.15)'
                                                        : '0 8px 24px rgba(13,71,161,0.4)',
                                                    background: mode === 'light'
                                                        ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                                                        : 'linear-gradient(135deg, rgba(13,71,161,0.15) 0%, rgba(21,101,192,0.08) 100%)',
                                                }
                                            }}
                                        >
                                            <WhatsAppIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            </Container>
                        </Box>

                        {/* Copyright Section */}
                        <Box
                            sx={{
                                py: 2,
                                px: 2,
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, rgba(227,242,253,0.8) 0%, rgba(187,222,251,0.6) 100%)'
                                    : 'transparent',
                                backdropFilter: mode === 'light' ? 'blur(10px)' : 'none',
                                border: mode === 'light'
                                    ? '1px solid rgba(255,255,255,0.2)'
                                    : 'none',
                                borderRadius: mode === 'light' ? 3 : 0,
                                mx: mode === 'light' ? 2 : 0,
                                boxShadow: mode === 'light'
                                    ? '0 8px 32px rgba(0,0,0,0.1)'
                                    : 'none',
                                position: 'relative',
                                ...(mode === 'dark' ? {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                                    },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.55), transparent)'
                                    },
                                } : {})
                            }}
                        >
                            <Container maxWidth="sm">
                                <Typography 
                                    variant="body2" 
                                    align="center"
                                    sx={{
                                        color: mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'primary.main',
                                        mt: 2,
                                        mb: 2,
                                        letterSpacing: 1,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        transition: 'all 0.25s',
                                        animation: 'fadeIn 2s ease-in-out infinite alternate',
                                        '@keyframes fadeIn': {
                                            '0%': {
                                                opacity: 0.7,
                                            },
                                            '100%': {
                                                opacity: 1,
                                            },
                                        },
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            textShadow: mode === 'light' 
                                                ? '0 0 8px rgba(0,0,0,0.3)'
                                                : '0 0 8px rgba(255,255,255,0.3)',
                                            animation: 'pulse 0.6s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(1.05)',
                                                },
                                                '50%': {
                                                    transform: 'scale(1.08)',
                                                },
                                                '100%': {
                                                    transform: 'scale(1.05)',
                                                },
                                            },
                                        }
                                    }}
                                >
                                    © 2025 RaketerS. All rights reserved.
                                </Typography>
                            </Container>
                        </Box>
                    </Box>
                </Box>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

// Komponen App yang membungkus Router
const App: React.FC = () => {
    return (
        <HelmetProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AppContent />
            </Router>
        </HelmetProvider>
    );
};

export default App; 